import React, { useEffect, useState } from "react";
import { styled } from '@mui/material/styles';
import { Chip, Button } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClone } from "@fortawesome/free-regular-svg-icons";
import Navigation from "./Navigation";
import Map from "./Map";
import { parseSearch } from "../../actions/helpers";
import { getResults } from "../../actions/results";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import earthquakeIcon from "../../../assets/images/earthquake.png";
import floodIcon from "../../../assets/images/flood.png";
import hurricaneIcon from "../../../assets/images/hurricane.png";
import tsunamiIcon from "../../../assets/images/tsunami.png";
import Snackbar from "@mui/material/Snackbar";
import Grow from "@mui/material/Grow";
import Fade from "@mui/material/Fade";
import AnalysisPinned from "./AnalysisPinned";
import Moment from "react-moment";

const PREFIX = 'Study';

const classes = {
  root: `${PREFIX}-root`,
  content: `${PREFIX}-content`,
  info: `${PREFIX}-info`,
  header: `${PREFIX}-header`,
  subheader: `${PREFIX}-subheader`,
  details: `${PREFIX}-details`,
  chips: `${PREFIX}-chips`,
  typeChip: `${PREFIX}-typeChip`,
  chip: `${PREFIX}-chip`,
  chipInverse: `${PREFIX}-chipInverse`,
  hazards: `${PREFIX}-hazards`,
  hazard: `${PREFIX}-hazard`,
  hazardImage: `${PREFIX}-hazardImage`,
  hazardTitle: `${PREFIX}-hazardTitle`,
  copyURL: `${PREFIX}-copyURL`,
  copyURLIcon: `${PREFIX}-copyURLIcon`,
  snack: `${PREFIX}-snack`,
  analysisPinnedContainer: `${PREFIX}-analysisPinnedContainer`
};

const Root = styled('div')((
  {
    theme
  }
) => ({

  [`& .${classes.content}`]: {
    display: "flex",
    height: "55vh",
    [theme.breakpoints.down("sm")]: {
      height: "auto",
      flexDirection: "column-reverse",
    },
  },

  [`& .${classes.info}`]: {
    flex: 1.3,
    display: "flex",
    flexDirection: "column",
    marginLeft: "2rem",
    [theme.breakpoints.down("sm")]: {
      flex: "auto",
      marginLeft: "0",
    },
  },

  [`& .${classes.header}`]: {
    fontSize: "2rem",
    borderBottom: "2px solid #d3d3d3",
    display: "block",
    marginBottom: "0.5em",
    padding: "0.2em 0",
  },

  [`& .${classes.subheader}`]: {
    fontWeight: "bold",
  },

  [`& .${classes.details}`]: {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
    flex: 1,
  },

  [`& .${classes.chips}`]: {
    position: "relative",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
  },

  [`& .${classes.typeChip}`]: {
    background: "#999",
    color: "#fff",
    marginRight: "0.5em",
  },

  [`& .${classes.chip}`]: {
    border: "1px solid #e9e9e9",
    background: "#fff",
    color: "#757575",
    margin: "0.2em",
    textTransform: "capitalize",
  },

  [`& .${classes.chipInverse}`]: {
    background: "#999999",
    color: "#fff",
    margin: "0.2em",
    textTransform: "capitalize",
  },

  [`& .${classes.hazards}`]: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: "0.5em",
  },

  [`&.${classes.hazard}`]: {
    display: "grid",
    margin: "0.5em 1em 0.5em 0em",
    height: "4em",
  },

  [`& .${classes.hazardImage}`]: {
    height: "1.6em",
    margin: "auto",
  },

  [`& .${classes.hazardTitle}`]: {
    fontSize: "0.8rem",
    color: "#757575",
    textTransform: "capitalize",
  },

  [`& .${classes.copyURL}`]: {
    background: "#fff",
    color: "#005287",
    margin: "0 0.1rem",
    fontSize: "0.8rem",
    "&:hover": {
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      background: "#fff",
    },
  },

  [`& .${classes.copyURLIcon}`]: {
    marginRight: "0.3em",
  },

  [`& .${classes.snack}`]: {
    "&.MuiSnackbar-root": {
      position: "absolute",
      justifyContent: "inherit",
    },
    "& .MuiSnackbarContent-root": {
      color: "#909090",
      background: "#fff",
    },
    "& .MuiSnackbar-anchorOriginTopCenter": {
      top: "10px",
    },
  },

  [`& .${classes.analysisPinnedContainer}`]: {
    flex: 1,
    display: "flex",
  }
}));

const Study = ({ results, getResults, details }) => {

  const [meta, setMeta] = useState();
  const [snack, setSnack] = useState({
    open: false,
    Transition: Fade,
  });
  const [analysisPinned, setAnalysisPinned] = useState(null);
  const { id, type } = parseSearch();

  const hazardIcons = {
    earthquake: earthquakeIcon,
    flood: floodIcon,
    hurricane: hurricaneIcon,
    tsunami: tsunamiIcon,
  };

  function GrowTransition(props) {
    return <Grow {...props} />;
  }

  const snackHandleClose = () => {
    setSnack({
      ...snack,
      open: false,
    });
  };

  const snackHandleClick = (Transition) => () => {
    navigator.clipboard.writeText(window.location.href);
    setSnack({
      open: true,
      Transition,
    });
  };

  const Hazard = ({ name, image }) => {
    return (
      <Root className={classes.hazard}>
        <img src={image} alt={name} className={classes.hazardImage} />
        <span className={classes.hazardTitle}>{name}</span>
      </Root>
    );
  };

  const copyURL = (Transition) => {
    navigator.clipboard.writeText(window.location.href);
    snackHandleClick(Transition);
    window.setTimeout(2);
    snackHandleClose();
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (results.length) {
      const metadata = results
        .filter((result) => result.type === type)
        .filter((result) => result.id === id)[0];
      setMeta(metadata);
    } else {
      getResults();
    }
  }, []);

  useEffect(() => {
    if (details.analyses.length > 0) {
      const pinned = details.analyses.filter((analysis) => analysis.pinned);
      if (pinned.length > 0) setAnalysisPinned(pinned[0]);
    }
  }, [details]);

  return (
    <Root className={classes.root}
	sx={{
		marginBottom: "3em",
	}}>
		<div>
	      <Navigation />
	      <div className={classes.content}>
	        <Map id={id} />
	        <div className={classes.info}>
	          <span className={classes.header}>{details ? details.name : ""}</span>
	          <div className={classes.details}>
	            <div className={classes.chips}>
	              {details && details.scale ? (
	                <Chip
	                  label={details.scale}
	                  size="small"
	                  className={classes.chipInverse}
	                />
	              ) : (
	                ""
	              )}
	              {details && details.type === "event" ? (
	                <Chip
	                  label={details.type}
	                  size="small"
	                  className={classes.chipInverse}
	                />
	              ) : (
	                ""
	              )}
	              {details &&
	              details.date &&
	              details.type === "event" &&
	              details.date.length > 0 ? (
	                <Chip
	                  label={
	                    <Moment format="MM/DD/YYYY">
	                      {details.date.toString()}
	                    </Moment>
	                  }
	                  size="small"
	                  className={classes.chip}
	                />
	              ) : (
	                ""
	              )}
	              <Button
	                className={classes.copyURL}
	                onClick={snackHandleClick(GrowTransition)}
	                aria-label="copy page url"
	              >
	                <FontAwesomeIcon
	                  className={classes.copyURLIcon}
	                  icon={faClone}
	                />
	                Copy Link
	              </Button>
	              <Snackbar
	                className={classes.snack}
	                open={snack.open}
	                onClose={snackHandleClose}
	                TransitionComponent={snack.Transition}
	                message="Page link copied to clipboard"
	                key={snack.Transition.name}
	                autoHideDuration={1500}
	                anchorOrigin={{ vertical: "top", horizontal: "center" }}
	              />
	            </div>
	            <div className={classes.analysisPinnedContainer}>
	              {analysisPinned ? (
	                <AnalysisPinned analysis={analysisPinned} />
	              ) : null}
	            </div>
	          </div>
	        </div>
	      </div>
		</div>
    </Root>
  );
};

Study.propTypes = {
  getResults: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  results: state.results.filtered,
  details: state.details,
});

export default connect(mapStateToProps, { getResults })(Study);
